import { Formik } from 'formik';
import { useState, useCallback, useContext, useEffect, useRef } from 'react';
import { Form, Button, Modal } from 'react-bootstrap';
import * as yup from 'yup';
import Common from '../../shared/Common';
import type { ILocation } from '../../../interfaces/ILocation';
import type { IIncomeCalendarSynchronization } from '../../../interfaces/IIncomeCalendarSynchronization';
import { DictionaryContext } from '../../../App';
import type { IIncomeCategory } from '../../../interfaces/IIncomeCategory';
import type { IIncomeQuantityType } from '../../../interfaces/IIncomeQuantityType';
import type { IIncome } from '../../../interfaces/IIncome';
import PerformIncomeCalendarSynchronizationIable from './PerformIncomeCalendarSynchronizationIable';

import './style.css';

export interface IPerformIncomeCalendarsSynchronizationModalProps {
  show: boolean;
  onHide: () => void;
  selectedIncomeCalendarSynchronization: IIncomeCalendarSynchronization | null;
  incomeQuantityTypes: IIncomeQuantityType[];
  incomeCategories: IIncomeCategory[];
  locationsNames: ILocation[];
}

function PerformIncomeCalendarsSynchronizationModal(props: IPerformIncomeCalendarsSynchronizationModalProps) {
  const [formLoading, setFormLoading] = useState<boolean>(false);
  const [incomes, setIncomes] = useState<IIncome[]>([]);
  const { reloadLocationNames } = useContext(DictionaryContext);
  const fetchIdRef = useRef(0);

  const date = new Date();
  const year = date.getFullYear();
  let month = date.getMonth().toString();
  if (month === '0') {
    month = '12';
  }
  if (month.length === 1) {
    month = '0' + month;
  }
  const [monthString, setMonthString] = useState(`${year}-${month}`);

  useEffect(() => {
    setMonthString(`${year}-${month}`);
  }, [props.show, month, year]);

  const fetchData = useCallback(async () => {
    if (!props.show) {
      return;
    }
    setFormLoading(true);
    const fetchId = ++fetchIdRef.current;
    const response = await Common.authorizedFetch(
      'api/CalendarSynchronizations/GetPerformIncomeCalendarSynchronizationData?' +
        `IncomeCalendarSynchronizationId=${props.selectedIncomeCalendarSynchronization?.id}&PerformMonth=${new Date(monthString).toISOString()}`
    );
    try {
      const data = await response.json();
      if (data.success) {
        if (fetchId === fetchIdRef.current) {
          if (data.result.incomes) {
            setIncomes(data.result.incomes);
          } else {
            setIncomes([]);
          }
        }
      } else {
        alert(data.errors);
      }
    } catch (e: any) {
      alert(e);
    } finally {
      setFormLoading(false);
    }
  }, [monthString, props.show, props.selectedIncomeCalendarSynchronization]);

  useEffect(() => {
    fetchData();
  }, [monthString, props.show, fetchData]);

  const PerformIncomeCalendarSynchronizationForm = useCallback(() => {
    const validationSchema = yup.object().shape({
      performMonth: yup.string().required('Pole jest wymagane'),
    });

    return (
      <Formik
        initialValues={{
          performMonth: monthString ?? '',
          items: incomes ?? [],
        }}
        validationSchema={validationSchema}
        onSubmit={async (values, { setSubmitting }) => {
          setSubmitting(true);
          setFormLoading(true);

          const requestBody = JSON.stringify({
            incomes: values.items,
            incomeCalendarSynchronizationId: props.selectedIncomeCalendarSynchronization?.id,
          });
          const apiMethod = 'api/calendarSynchronizations/PerformIncomeCalendarSynchronization';

          const requestOptions = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: requestBody,
          };
          try {
            const response = await Common.authorizedFetch(apiMethod, requestOptions);
            const data = await response.json();
            if (!data.success) {
              alert(data.errors);
            } else {
              if (data.result.newLocationAdded && reloadLocationNames) {
                reloadLocationNames();
              }
            }
          } catch (e) {
            alert(e);
          } finally {
            props.onHide();
            setFormLoading(false);
            setSubmitting(false);
          }
        }}
      >
        {({ values, errors, touched, handleBlur, handleSubmit, setFieldValue }) => (
          <>
            <Form id='calendar-synchronization-form' onSubmit={handleSubmit}>
              <Form.Group className='mt-1'>
                <Form.Label>Data przychodów</Form.Label>
                <Form.Control
                  type='month'
                  min='2018-01'
                  disabled={formLoading}
                  placeholder='Wpisz interwał'
                  onChange={(e: any) => {
                    setFieldValue('performMonth', e.target.value);
                    setMonthString(e.target.value);
                  }}
                  onBlur={handleBlur}
                  value={monthString}
                  className={touched.performMonth && errors.performMonth ? 'error' : undefined}
                />
                {touched.performMonth && errors.performMonth ? (
                  <div className='error-message'>{errors.performMonth}</div>
                ) : null}
                <Form.Text className='text-muted'>Miesiąc za który mają zostać wygenerowane przychody.</Form.Text>
              </Form.Group>
              {!formLoading && (
                <Form.Group>
                  <PerformIncomeCalendarSynchronizationIable
                    items={values.items}
                    setItems={(items) => {
                      setFieldValue('items', items);
                    }}
                    quantityTypes={props.incomeQuantityTypes}
                    categories={props.incomeCategories}
                    locations={props.locationsNames}
                    updateIncomeCalendarSynchronization={(rowIndex: number, columnId: string, value: any) => {
                      const updatedItems = [...values.items];
                      updatedItems[rowIndex] = {
                        ...updatedItems[rowIndex],
                        [columnId]: value,
                      };
                      setFieldValue('items', updatedItems);
                    }}
                  />
                </Form.Group>
              )}
            </Form>
          </>
        )}
      </Formik>
    );
  }, [props, reloadLocationNames, setMonthString, incomes, monthString, formLoading]);

  return (
    <>
      <Modal
        show={props.show}
        onHide={props.onHide}
        aria-labelledby='contained-modal-title-vcenter'
        size='lg'
        style={{ maxWidth: '100%', overflowX: 'auto' }}
      >
        <Modal.Header closeButton>
          <Modal.Title id='contained-modal-title-vcenter'>Wykonaj synchronizację kalendarza</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {Common.Ui.showLoadingSpinnerAbsolute(formLoading)}
          <PerformIncomeCalendarSynchronizationForm />
        </Modal.Body>
        <Modal.Footer>
          <Button variant='dark' onClick={props.onHide}>
            Zamknij
          </Button>
          <Button variant='success' type='submit' form='calendar-synchronization-form' disabled={formLoading}>
            Zapisz
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default PerformIncomeCalendarsSynchronizationModal;
